import { Card, Typography, Space, Tag, Collapse } from "antd";
import useIsMobile from "@/hooks/useIsMobile";
import { useIntl } from "react-intl";
import { HotelRoom } from "@/AppRedux/actions/hotelProfile/hotelType";
import RoomPhotoGrid from "@/modules/booking/components/RoomPhotoGrid";
import { HtmlText } from "@/components/HtmlText";

export default function HotelRoomOverview({ rooms }: { rooms?: HotelRoom[] }) {
  const isMobile = useIsMobile(768);
  const intl = useIntl();

  if (!rooms || rooms.length === 0) {
    return null;
  }

  // 📱 Mobile: ใช้สไตล์เดียวกับ RoomCard mobile
  if (isMobile) {
    return (
      <Space orientation="vertical" size={16} style={{ width: "100%" }}>
        {rooms.map((r) => (
          <Card
            key={r.id}
            style={{ borderRadius: 16, overflow: "hidden", padding: 0, cursor: "text" }}
            styles={{
              body: {
                padding: 0
              }
            }}
            hoverable
          >
            {/* รูปเต็มความกว้างด้านบน */}
            {r.thumbnail && <RoomPhotoGrid
              images={r.images}
              thumbnail={r.thumbnail}
              name={r.name}
              variant="mobile"
            />}

            {/* เนื้อหาด้านล่าง */}
            <div style={{ padding: 16 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {r.name}
              </Typography.Title>

              {r.short && (
                <Typography.Paragraph
                  type="secondary"
                  style={{
                    marginTop: 6,
                    marginBottom: 10,
                    fontSize: "0.95rem",
                  }}
                >
                  <HtmlText value={r.short} />
                </Typography.Paragraph>
              )}

              {!!r.highlights?.length && (
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text strong>
                    {intl.formatMessage({ id: "room.highlights" })}:{" "}
                  </Typography.Text>
                  <Space size={[6, 6]} wrap>
                    {r.highlights!.map((h) => (
                      <Tag variant="filled" key={h}>
                        {h}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {!!r.amenities?.length && (
                <Collapse
                  ghost
                  size="small"
                  items={[
                    {
                      key: "amenities",
                      label: intl.formatMessage({ id: "room.facilities" }),
                      children: (
                        <Space size={[6, 6]} wrap>
                          {r.amenities!.map((a) => (
                            <Tag key={a}>{a}</Tag>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                  style={{ marginTop: 4, marginLeft: -8, marginRight: -8 }}
                />
              )}
            </div>
          </Card>
        ))}
      </Space>
    );
  }
  // 🖥 Desktop: ใช้โครงเดิม (รูปซ้าย / ขวาเป็นรายละเอียด)
  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      {rooms.map((r) => (
        <Card
          key={r.id}
          styles={{
            root: { borderRadius: 16, cursor: "text" },
            body: { padding: 16 }
          }}
          hoverable
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: 16,
              alignItems: "flex-start",
            }}
          >
            {r.thumbnail ? <RoomPhotoGrid
              images={r.images}
              thumbnail={r.thumbnail}
              name={r.name}
              variant="desktop"
            /> : <div />}
            <div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {r.name}
              </Typography.Title>
              <Typography.Paragraph
                type="secondary"
                style={{ margin: "4px 0 8px" }}
              >
                <HtmlText value={r.short} />
              </Typography.Paragraph>

              {!!r.highlights?.length && (
                <div style={{ marginBottom: 6 }}>
                  <Typography.Text strong style={{ marginBottom: 8, marginTop: 8, display: "block" }}>
                    {intl.formatMessage({ id: "room.highlights" })}{" "}
                  </Typography.Text>
                  <Space size={[6, 6]} wrap>
                    {r.highlights!.map((h) => (
                      <Tag key={h}>{h}</Tag>
                    ))}
                  </Space>
                </div>
              )}

              {!!r.amenities?.length && (
                <div>
                  <Typography.Text strong style={{ marginBottom: 8, marginTop: 8, display: "block" }}>
                    {intl.formatMessage({ id: "room.facilities" })}{" "}
                  </Typography.Text>
                  <Space size={[6, 6]} wrap>
                    {r.amenities!.map((a) => (
                      <Tag key={a}>{a}</Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}
    </Space>
  );
}